import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Select,
  Textarea,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowDown20Regular,
  ArrowDownloadRegular,
  ArrowRepeatAll16Regular,
  ArrowRepeatAll20Regular,
  ArrowUp20Regular,
  Building20Filled,
  CalendarAdd20Regular,
  CalendarCheckmark20Regular,
  CalendarDay20Regular,
  CalendarLtr20Regular,
  CalendarMonth20Regular,
  CalendarToday20Regular,
  CalendarWeekStart20Regular,
  CalendarWorkWeek20Regular,
  CheckmarkCircle20Filled,
  ChevronDown12Regular,
  ChevronDown16Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Circle20Regular,
  Clock20Regular,
  Delete20Regular,
  DocumentArrowDownRegular,
  DocumentDataLinkRegular,
  DocumentFlowchartRegular,
  Dismiss20Regular,
  Edit20Regular,
  Filter20Regular,
  Home20Filled,
  LinkRegular,
  Location20Regular,
  Navigation20Regular,
  People20Regular,
  Pin16Regular,
  Print20Regular,
  Settings16Regular,
  Share20Regular,
  SplitHorizontal20Regular,
  TextDescription20Regular,
  Timer16Regular,
  Video20Regular,
} from "@fluentui/react-icons";
import NOBOOKEDINGITEM from "../../assets/images/illustration.svg";
import calender from "../../assets/images/Calendar.svg";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import styles from "./Calender.module.css";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
type ViewKey = "day" | "workweek" | "week" | "month";
type Repeat = "none" | "daily" | "weekdays" | "weekly" | "monthly" | "yearly" | "custom";
type WorkLoc = "home" | "office" | "remote" | "travel";
type FilterKey = "all" | "recurring" | "once";

interface CalDef {
  id: string;
  name: string;
  color: string;
  on: boolean;
  canEdit: boolean;
}

/** A person on an event. Name-only guests have an empty email and are never e-mailed. */
interface Guest {
  name: string;
  email: string;
  userId: string | null;
}

/** One occurrence of an event, as the calendar service returns it. Times are minutes from midnight (your local time). */
interface Ev {
  id: string; // the event's id on the server (a repeating event keeps one id for all its dates)
  calId: string;
  title: string;
  date: string; // yyyy-mm-dd of THIS occurrence
  seriesDate: string; // yyyy-mm-dd where the series starts
  startAt: string; // ISO start of this occurrence (addresses one date of a series)
  start: number;
  end: number;
  repeat: Repeat;
  recurring: boolean;
  location: string;
  online: boolean;
  onlineUrl: string;
  organizer: string;
  attendees: string[];
  guests: Guest[];
  notes: string;
  canEdit: boolean;
}

/** One occurrence of an event on a given day, with its overlap column. */
interface Occ {
  ev: Ev;
  key: string; // yyyy-mm-dd
  day: Date;
  endAt: Date;
  col: number;
  cols: number;
}

interface Pane {
  kind: "event" | "day";
  date: string;
  evId: string | null;
}

interface Draft {
  id: string | null;
  title: string;
  calId: string;
  date: string;
  start: string;
  end: string;
  repeat: Repeat;
  was: Repeat; // the repeat the event had when the form opened
  location: string;
  online: boolean;
  attendees: string;
  notes: string;
}

interface ToastState {
  text: string;
  undo?: (() => void) | undefined;
}

/* ------------------------------------------------------------------ */
/* Constants + date helpers                                            */
/* ------------------------------------------------------------------ */
const HOUR_H = 80; // px per hour in the time grid
const TEAMS = "Microsoft Teams Meeting";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DOW_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_INITIAL = ["S", "M", "T", "W", "T", "F", "S"];
const SWATCHES = ["#0f6cbd", "#00a858", "#f7630c", "#8764b8", "#c4314b", "#038387"];

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d: Date) => addDays(d, -d.getDay()); // Sunday
const mondayOf = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));
const sameDay = (a: Date, b: Date) => ymd(a) === ymd(b);
const hhmm = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const toMin = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
const shortDate = (d: Date) => `${DOW_SHORT[d.getDay()] ?? ""}, ${(MONTHS[d.getMonth()] ?? "").slice(0, 3)} ${d.getDate()}`;
const longDate = (d: Date) => `${DOW_LONG[d.getDay()] ?? ""}, ${d.getDate()} ${MONTHS[d.getMonth()] ?? ""} ${d.getFullYear()}`;

const repeatText = (e: Ev): string => {
  if (e.repeat === "daily") return "Repeats every day";
  if (e.repeat === "weekdays") return "Repeats every weekday";
  if (e.repeat === "weekly") return `Repeats every ${DOW_LONG[parseYmd(e.seriesDate).getDay()] ?? "week"}`;
  if (e.repeat === "monthly") return "Repeats every month";
  if (e.repeat === "yearly") return "Repeats every year";
  if (e.repeat === "custom") return "Repeats on a custom schedule";
  return "";
};

/** Packs a day's occurrences into side-by-side columns when they overlap. */
const layoutDay = (items: Occ[]): Occ[] => {
  const sorted = [...items].sort((a, b) => a.ev.start - b.ev.start || b.ev.end - a.ev.end);
  const out: Occ[] = [];
  let cluster: Occ[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const n = Math.max(colEnds.length, 1);
    cluster.forEach((o) => {
      o.cols = n;
    });
    out.push(...cluster);
    cluster = [];
    colEnds = [];
    clusterEnd = -1;
  };
  for (const it of sorted) {
    if (cluster.length > 0 && it.ev.start >= clusterEnd) flush();
    let c = colEnds.findIndex((e) => e <= it.ev.start);
    if (c < 0) {
      c = colEnds.length;
      colEnds.push(it.ev.end);
    } else {
      colEnds[c] = it.ev.end;
    }
    cluster.push({ ...it, col: c, cols: 1 });
    clusterEnd = Math.max(clusterEnd, it.ev.end);
  }
  flush();
  return out;
};

const makeOcc = (ev: Ev, day: Date): Occ => ({
  ev,
  key: ymd(day),
  day,
  endAt: new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, ev.end),
  col: 0,
  cols: 1,
});

/* ------------------------------------------------------------------ */
/* Calendar service (calender.cilpron.com)                              */
/* ------------------------------------------------------------------ */
const CAL_BASE_URL = "https://calender.cilpron.com";
const TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Lagos";
  } catch {
    return "Africa/Lagos";
  }
})();

const readToken = (): string | null => {
  try {
    return localStorage.getItem("authToken");
  } catch {
    return null;
  }
};

/** The signed-in person's id (the token's `sub`). Only used to show "You" as organizer; never for security. */
const tokenSub = (token: string | null): string | undefined => {
  try {
    if (!token) return undefined;
    const p = token.split(".")[1] ?? "";
    const json = decodeURIComponent(
      atob(p.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const o = JSON.parse(json) as Record<string, string>;
    return (o.sub ?? o.nameid ?? o["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"])?.toLowerCase();
  } catch {
    return undefined;
  }
};

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const enc = encodeURIComponent;

async function call<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`${CAL_BASE_URL}/api/calendar${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "Couldn’t reach the calendar. Check your connection.");
  }
  if (res.status === 401) throw new ApiError(401, "Your session ended. Sign in again.");
  if (res.status === 403) throw new ApiError(403, "You don’t have permission to do that.");
  if (!res.ok) {
    let msg = "";
    try {
      const j = (await res.json()) as Record<string, unknown>;
      const pick = j.detail ?? j.message ?? j.title ?? j.error;
      if (typeof pick === "string") msg = pick;
    } catch {
      /* no readable body */
    }
    throw new ApiError(res.status, res.status === 400 ? msg || "That could not be saved. Check the entries and try again." : msg || `The calendar answered with an error (${res.status}).`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const msgOf = (e: unknown): string => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

interface ApiGuest {
  name?: string | null;
  email?: string | null;
  userId?: string | null;
}
interface ApiEvent {
  id: string;
  calendarId: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  seriesStartAt?: string | null;
  isAllDay: boolean;
  repeat?: string;
  isRecurring: boolean;
  organizerName?: string | null;
  createdBy?: string;
  onlineProvider?: string | null;
  onlineUrl?: string | null;
  canEdit: boolean;
  attendees?: ApiGuest[];
}
interface ApiCalendar {
  id: string;
  name: string;
  color?: string | null;
  role?: string;
}
interface ApiWork {
  day: string;
  location: string;
}

const REPEATS: Repeat[] = ["none", "daily", "weekdays", "weekly", "monthly", "yearly", "custom"];
const WORKLOCS: WorkLoc[] = ["home", "office", "remote", "travel"];
const minutesOf = (d: Date) => d.getHours() * 60 + d.getMinutes();

/** One occurrence from the feed -> what the screen draws. */
const toEv = (a: ApiEvent, me: string | undefined): Ev => {
  const s = new Date(a.startAt);
  const e = new Date(a.endAt);
  const date = a.isAllDay ? a.startAt.slice(0, 10) : ymd(s);
  const start = a.isAllDay ? 0 : minutesOf(s);
  let end = a.isAllDay ? 24 * 60 : ymd(e) === date ? minutesOf(e) : 24 * 60; // an event that runs past midnight is drawn up to midnight
  if (end <= start) end = Math.min(start + 15, 24 * 60);
  const series = a.seriesStartAt ? new Date(a.seriesStartAt) : s;
  const provider = (a.onlineProvider ?? "none").toLowerCase();
  const online = provider !== "none" && provider !== "";
  const guests: Guest[] = (a.attendees ?? [])
    .map((g) => ({ name: (g.name ?? "").trim(), email: (g.email ?? "").trim(), userId: g.userId ?? null }))
    .filter((g) => g.name || g.email);
  const repeat = (REPEATS.includes(a.repeat as Repeat) ? a.repeat : a.isRecurring ? "custom" : "none") as Repeat;
  return {
    id: a.id,
    calId: a.calendarId,
    title: a.title,
    date,
    seriesDate: a.isAllDay ? (a.seriesStartAt ?? a.startAt).slice(0, 10) : ymd(series),
    startAt: a.startAt,
    start,
    end,
    repeat,
    recurring: a.isRecurring,
    location: (a.location ?? "") || (online ? (provider === "teams" ? TEAMS : "Online meeting") : ""),
    online,
    onlineUrl: a.onlineUrl ?? "",
    organizer: me && (a.createdBy ?? "").toLowerCase() === me ? "You" : a.organizerName || "Organizer",
    attendees: guests.map((g) => g.name || g.email),
    guests,
    notes: a.description ?? "",
    canEdit: a.canEdit,
  };
};

const toCal = (c: ApiCalendar, i: number, was: CalDef | undefined): CalDef => ({
  id: c.id,
  name: c.name,
  color: c.color || SWATCHES[i % SWATCHES.length] || "#0f6cbd",
  on: was ? was.on : true,
  canEdit: ["owner", "manager", "editor"].includes((c.role ?? "").toLowerCase()),
});

/* ------------------------------------------------------------------ */
/* Mini month (left sidebar + "jump to date" popover)                  */
/* ------------------------------------------------------------------ */
const MiniMonth: React.FC<{
  cursor: Date;
  anchor: Date;
  today: Date;
  rangeStart: Date;
  rangeEnd: Date;
  highlight: boolean;
  open: boolean;
  onPick: (d: Date) => void;
  onMove: (n: number) => void;
  onToggle: () => void;
  hideToggle?: boolean;
}> = ({ cursor, anchor, today, rangeStart, rangeEnd, highlight, open, onPick, onMove, onToggle, hideToggle }) => {
  const first = open ? startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1)) : startOfWeek(anchor);
  const weeks = Array.from({ length: open ? 6 : 1 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(first, w * 7 + i)));
  const rs = ymd(rangeStart);
  const re = ymd(rangeEnd);
  return (
    <div className={styles.calMini}>
      <div className={styles.calMiniHead}>
        {!hideToggle && <Button appearance="subtle" size="small" icon={open ? <ChevronDown16Regular /> : <ChevronRight20Regular />} aria-label={open ? "Collapse month" : "Expand month"} aria-expanded={open} onClick={onToggle} />}
        <span className={styles.calMiniTitle}>{cursor.getFullYear()} {MONTHS[cursor.getMonth()]}</span>
        <Button appearance="subtle" size="small" icon={<ArrowUp20Regular />} aria-label="Previous month" onClick={() => onMove(-1)} />
        <Button appearance="subtle" size="small" icon={<ArrowDown20Regular />} aria-label="Next month" onClick={() => onMove(1)} />
      </div>
      <div className={styles.calMiniDow} aria-hidden="true">
        {DOW_INITIAL.map((c, i) => <span key={i}>{c}</span>)}
      </div>
      {weeks.map((wk, wi) => {
        const inRange = highlight && wk.some((d) => ymd(d) >= rs && ymd(d) <= re);
        return (
          <div key={wi} className={`${styles.calMiniRow} ${inRange ? styles.calMiniRowOn : ""}`} role="row">
            {wk.map((d) => {
              const isToday = sameDay(d, today);
              const isSel = sameDay(d, anchor);
              return (
                <button
                  key={ymd(d)}
                  type="button"
                  role="gridcell"
                  aria-label={longDate(d)}
                  aria-current={isToday ? "date" : undefined}
                  className={`${styles.calMiniDay} ${isToday ? styles.calMiniToday : isSel ? styles.calMiniSel : ""}`}
                  onClick={() => onPick(d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* New / edit event dialog                                             */
/* ------------------------------------------------------------------ */
const EventDialog: React.FC<{
  draft: Draft | null;
  cals: CalDef[];
  error: string;
  recurring: boolean;
  onChange: (p: Partial<Draft>) => void;
  onSave: () => void;
  onCancel: () => void;
}> = ({ draft, cals, error, recurring, onChange, onSave, onCancel }) => (
  <Dialog open={draft !== null} onOpenChange={(_, d) => { if (!d.open) onCancel(); }}>
    <DialogSurface className={styles.calDialog}>
      {draft && (
        <DialogBody>
          <DialogTitle>{draft.id === null ? "New event" : "Edit event"}</DialogTitle>
          <DialogContent>
            <div className={styles.calForm}>
              <Field label="Title" required className={styles.calFull}>
                <Input autoFocus value={draft.title} placeholder="Add a title" onChange={(_, d) => onChange({ title: d.value })} onKeyDown={(e) => { if (e.key === "Enter") onSave(); }} />
              </Field>
              <Field label={recurring ? "Series starts" : "Date"}>
                <Input type="date" value={draft.date} onChange={(_, d) => onChange({ date: d.value })} />
              </Field>
              <Field label="Calendar">
                <Select value={draft.calId} onChange={(_, d) => onChange({ calId: d.value })}>
                  {cals.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Start">
                <Input type="time" step={900} value={draft.start} onChange={(_, d) => onChange({ start: d.value })} />
              </Field>
              <Field label="End">
                <Input type="time" step={900} value={draft.end} onChange={(_, d) => onChange({ end: d.value })} />
              </Field>
              <Field label="Repeat">
                <Select value={draft.repeat} onChange={(_, d) => onChange({ repeat: d.value as Repeat })}>
                  <option value="none">Does not repeat</option>
                  <option value="daily">Every day</option>
                  <option value="weekdays">Every weekday</option>
                  <option value="weekly">Every week</option>
                  <option value="monthly">Every month</option>
                  <option value="yearly">Every year</option>
                  {draft.repeat === "custom" && <option value="custom">Custom repeat (unchanged)</option>}
                </Select>
              </Field>
              <Field label="Online meeting">
                <Select value={draft.online ? "yes" : "no"} onChange={(_, d) => onChange({ online: d.value === "yes", location: d.value === "yes" ? TEAMS : draft.location === TEAMS ? "" : draft.location })}>
                  <option value="no">None</option>
                  <option value="yes">Microsoft Teams</option>
                </Select>
              </Field>
              <Field label="Location" className={styles.calFull}>
                <Input value={draft.location} placeholder="Add a location" onChange={(_, d) => onChange({ location: d.value })} />
              </Field>
              <Field label="Attendees" hint="Separate names with commas" className={styles.calFull}>
                <Input value={draft.attendees} placeholder="Add people" onChange={(_, d) => onChange({ attendees: d.value })} />
              </Field>
              <Field label="Description" className={styles.calFull}>
                <Textarea resize="vertical" rows={3} value={draft.notes} onChange={(_, d) => onChange({ notes: d.value })} />
              </Field>
            </div>
            {error && <div className={styles.calErr} role="alert">{error}</div>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" size="small" onClick={onCancel}>Cancel</Button>
            <Button appearance="primary" size="small" onClick={onSave}>Save</Button>
          </DialogActions>
        </DialogBody>
      )}
    </DialogSurface>
  </Dialog>
);

/* ------------------------------------------------------------------ */
/* Details shown inside the slide-out panel                            */
/* ------------------------------------------------------------------ */
const EventDetails: React.FC<{
  occ: Occ;
  cal: CalDef | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onJoin: () => void;
}> = ({ occ, cal, onEdit, onDelete, onJoin }) => {
  const e = occ.ev;
  return (
    <div className={styles.calDetail}>
      <div className={styles.calDetailActions}>
        {e.canEdit && <Button appearance="subtle" size="small" icon={<Edit20Regular />} onClick={onEdit}>Edit</Button>}
        {e.canEdit && <Button appearance="subtle" size="small" icon={<Delete20Regular />} className={styles.calDanger} onClick={onDelete}>Delete</Button>}
      </div>
      <h3 className={styles.calDetailTitle} style={{ borderLeftColor: cal?.color ?? "#0f6cbd" }}>{e.title}</h3>
      <div className={styles.calRow}>
        <Clock20Regular />
        <div>
          <div>{longDate(occ.day)}</div>
          <div className={styles.calMuted}>{hhmm(e.start)} – {hhmm(e.end)}</div>
        </div>
      </div>
      {e.recurring && (
        <div className={styles.calRow}><ArrowRepeatAll20Regular /><div>{repeatText(e)}</div></div>
      )}
      {(e.location || e.online) && (
        <div className={styles.calRow}>
          {e.online ? <Video20Regular /> : <Location20Regular />}
          <div className={styles.calGrow}>
            <div>{e.location}</div>
            {e.online && <Button appearance="primary" size="small" className={styles.calJoin} onClick={onJoin}>Join</Button>}
          </div>
        </div>
      )}
      <div className={styles.calRow}>
        <People20Regular />
        <div className={styles.calGrow}>
          <div className={styles.calPerson}><Avatar name={e.organizer} size={24} /><span>{e.organizer}</span><em>Organizer</em></div>
          {e.attendees.map((a) => (
            <div key={a} className={styles.calPerson}><Avatar name={a} size={24} /><span>{a}</span></div>
          ))}
        </div>
      </div>
      <div className={styles.calRow}>
        <CalendarLtr20Regular />
        <div className={styles.calCalName}><i style={{ background: cal?.color ?? "#0f6cbd" }} />{cal?.name ?? "Calendar"}</div>
      </div>
      {e.notes && (
        <div className={styles.calRow}><TextDescription20Regular /><div className={styles.calNotes}>{e.notes}</div></div>
      )}
    </div>
  );
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const CilpronCalenders: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  const [view, setView] = useState<ViewKey>("workweek");
  const [dayN, setDayN] = useState(1);
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [cursor, setCursor] = useState<Date>(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [miniOpen, setMiniOpen] = useState(true);
  const [cals, setCals] = useState<CalDef[]>([]);
  const [events, setEvents] = useState<Ev[]>([]);
  const [workLoc, setWorkLoc] = useState<Record<string, WorkLoc>>({});
  const [filter, setFilter] = useState<FilterKey>("all");
  const [pane, setPane] = useState<Pane | null>(null);
  const [paneOpen, setPaneOpen] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftErr, setDraftErr] = useState("");
  const [addCal, setAddCal] = useState<{ name: string; color: string } | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [delAsk, setDelAsk] = useState<Ev | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<"loading" | "ready" | "signin" | "error">("loading");
  const [note, setNote] = useState("");
  const [live, setLive] = useState<"off" | "connecting" | "live" | "reconnecting">("off");
  const [token, setToken] = useState<string | null>(readToken);
  const rangeRef = useRef({ from: "", to: "", f: "", t: "" });
  const seq = useRef(0);
  const loadRef = useRef<(quiet?: boolean) => Promise<void>>(async () => undefined);
  const scrollRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => startOfDay(now), [now]);

  /* clock tick (past/future styling + the "now" line) */
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(t);
  }, []);

  const say = useCallback((text: string, undo?: () => void) => setToast(undo ? { text, undo } : { text }), []);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  /* Esc closes the details panel */
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !draft && !addCal) setPaneOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [draft, addCal]);

  /* the folder drawer closes itself when the window grows past the breakpoint */
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 901px)");
    const h = () => {
      if (mq.matches) setNavOpen(false);
    };
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  /* start the time grid around 08:00 */
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = HOUR_H * 8.4;
  }, [view]);

  const goTo = useCallback((d: Date) => {
    const s = startOfDay(d);
    setAnchor(s);
    setCursor(new Date(s.getFullYear(), s.getMonth(), 1));
  }, []);

  /* ---------- visible range ---------- */
  const days = useMemo<Date[]>(() => {
    if (view === "workweek") {
      const m = addDays(mondayOf(anchor), anchor.getDay() === 0 ? 7 : 0); // on a Sunday show the coming work week
      return Array.from({ length: 5 }, (_, i) => addDays(m, i));
    }
    if (view === "week") {
      const s = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(s, i));
    }
    if (view === "day") return Array.from({ length: dayN }, (_, i) => addDays(anchor, i));
    const s = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    return Array.from({ length: 42 }, (_, i) => addDays(s, i));
  }, [view, anchor, dayN]);
  const rangeStart = days[0] ?? anchor;
  const rangeEnd = days[days.length - 1] ?? anchor;
  rangeRef.current = { from: startOfDay(rangeStart).toISOString(), to: addDays(startOfDay(rangeEnd), 1).toISOString(), f: ymd(rangeStart), t: ymd(rangeEnd) };
  const rangeKey = `${ymd(rangeStart)}|${ymd(rangeEnd)}`;

  /* ---------- load what is on screen from the calendar service ---------- */
  const load = useCallback(async (quiet = false) => {
    if (!readToken()) {
      setNote("");
      setStatus("signin");
      return;
    }
    const mine = ++seq.current;
    if (!quiet) setStatus((s) => (s === "ready" ? s : "loading"));
    try {
      const r = rangeRef.current;
      const feed = await call<ApiEvent[]>("GET", `/feed?from=${enc(r.from)}&to=${enc(r.to)}`); // first: a new person gets a default calendar here
      const [list, work] = await Promise.all([
        call<ApiCalendar[]>("GET", "/calendars"),
        call<ApiWork[]>("GET", `/work-locations?from=${r.f}&to=${r.t}`),
      ]);
      if (mine !== seq.current) return;
      const me = tokenSub(readToken());
      setCals((prev) => list.map((c, i) => toCal(c, i, prev.find((p) => p.id === c.id))));
      setEvents(feed.map((a) => toEv(a, me)));
      const w: Record<string, WorkLoc> = {};
      for (const x of work) if (WORKLOCS.includes(x.location as WorkLoc)) w[x.day.slice(0, 10)] = x.location as WorkLoc;
      setWorkLoc(w);
      setNote("");
      setStatus("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setNote(msgOf(e));
      setStatus(e instanceof ApiError && e.status === 401 ? "signin" : "error");
    }
  }, []);
  loadRef.current = load;

  useEffect(() => {
    void load();
  }, [load, rangeKey, token]);

  /* a failed load tries again by itself */
  useEffect(() => {
    if (status !== "error") return;
    const t = window.setTimeout(() => void loadRef.current(true), 15000);
    return () => window.clearTimeout(t);
  }, [status]);

  /* pick up a new sign-in, and refresh when the tab is opened again */
  useEffect(() => {
    const t = window.setInterval(() => setToken((p) => (readToken() === p ? p : readToken())), 3000);
    const back = () => void loadRef.current(true);
    window.addEventListener("focus", back);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("focus", back);
    };
  }, []);

  /* live updates: the service says "changed" and the screen asks again */
  useEffect(() => {
    if (!token) {
      setLive("off");
      return;
    }
    let dead = false;
    let again = 0;
    let soon = 0;
    const conn = new HubConnectionBuilder()
      .withUrl(`${CAL_BASE_URL}/hubs/calendar`, { accessTokenFactory: () => readToken() ?? "" })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.None)
      .build();
    const refresh = () => {
      window.clearTimeout(soon);
      soon = window.setTimeout(() => void loadRef.current(true), 400);
    };
    const start = () => {
      if (dead) return;
      setLive("connecting");
      conn.start().catch(() => {
        if (!dead) {
          setLive("off");
          again = window.setTimeout(start, 10000);
        }
      });
    };
    conn.on("ready", () => setLive("live"));
    conn.on("changed", refresh);
    conn.onreconnecting(() => setLive("reconnecting"));
    conn.onreconnected(() => {
      setLive("live");
      refresh();
    });
    conn.onclose(() => {
      if (!dead) {
        setLive("off");
        again = window.setTimeout(start, 10000);
      }
    });
    start();
    return () => {
      dead = true;
      window.clearTimeout(again);
      window.clearTimeout(soon);
      void conn.stop();
    };
  }, [token]);

  const label = useMemo(() => {
    const a = rangeStart;
    const b = rangeEnd;
    const ma = MONTHS[a.getMonth()] ?? "";
    const mb = MONTHS[b.getMonth()] ?? "";
    if (view === "month") return `${MONTHS[anchor.getMonth()] ?? ""} ${anchor.getFullYear()}`;
    if (days.length === 1) return `${DOW_LONG[a.getDay()] ?? ""}, ${ma} ${a.getDate()}, ${a.getFullYear()}`;
    if (a.getFullYear() !== b.getFullYear()) return `${a.getFullYear()}, ${ma} ${a.getDate()} – ${b.getFullYear()}, ${mb} ${b.getDate()}`;
    if (a.getMonth() !== b.getMonth()) return `${a.getFullYear()}, ${ma} ${a.getDate()} – ${mb} ${b.getDate()}`;
    return `${a.getFullYear()}, ${ma} ${a.getDate()}–${b.getDate()}`;
  }, [view, anchor, days.length, rangeStart, rangeEnd]);

  const step = (dir: 1 | -1) => {
    if (view === "month") goTo(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
    else if (view === "day") goTo(addDays(anchor, dir * dayN));
    else goTo(addDays(anchor, dir * 7));
  };

  /* ---------- events for the visible days ---------- */
  const hidden = useMemo(() => new Set(cals.filter((c) => !c.on).map((c) => c.id)), [cals]);
  const byDate = useMemo(() => {
    const m = new Map<string, Ev[]>();
    for (const e of events) {
      if (hidden.has(e.calId)) continue;
      if (filter !== "all" && (filter === "recurring") !== e.recurring) continue;
      const l = m.get(e.date);
      if (l) l.push(e);
      else m.set(e.date, [e]);
    }
    return m;
  }, [events, hidden, filter]);
  const occFor = useCallback((d: Date): Occ[] => layoutDay((byDate.get(ymd(d)) ?? []).map((e) => makeOcc(e, d))), [byDate]);
  const byDay = useMemo(() => {
    const m = new Map<string, Occ[]>();
    days.forEach((d) => m.set(ymd(d), occFor(d)));
    return m;
  }, [days, occFor]);
  const calById = (id: string) => cals.find((c) => c.id === id);

  /* ---------- details panel ---------- */
  const openEvent = (o: Occ) => {
    setPane({ kind: "event", date: o.key, evId: o.ev.id });
    setPaneOpen(true);
  };
  const openDay = (d: Date) => {
    setPane({ kind: "day", date: ymd(d), evId: null });
    setPaneOpen(true);
  };
  const paneDay = pane ? parseYmd(pane.date) : null;
  const paneEv = pane && pane.evId !== null ? events.find((e) => e.id === pane.evId && e.date === pane.date) : undefined;
  const paneOcc = paneEv && paneDay ? makeOcc(paneEv, paneDay) : null;
  const paneList = pane && pane.kind === "day" && paneDay ? occFor(paneDay) : [];
  const selKey = paneOpen && pane && pane.kind === "event" ? `${pane.evId}|${pane.date}` : "";

  /* ---------- create / edit / delete ---------- */
  const writable = cals.filter((c) => c.canEdit);
  const blankDraft = (day: Date, startMin: number, online = false): Draft => ({
    id: null,
    title: "",
    calId: (writable.find((c) => c.on) ?? writable[0])?.id ?? "",
    date: ymd(day),
    start: hhmm(startMin),
    end: hhmm(Math.min(startMin + 30, 24 * 60 - 1)),
    repeat: "none",
    was: "none",
    location: online ? TEAMS : "",
    online,
    attendees: "",
    notes: "",
  });
  const openNew = (online = false) => {
    if (writable.length === 0) {
      say(status === "ready" ? "You have no calendar you can add events to." : "Your calendars are still loading.");
      return;
    }
    let s = 9 * 60;
    if (sameDay(anchor, today)) s = Math.min(Math.ceil((now.getHours() * 60 + now.getMinutes() + 1) / 30) * 30, 23 * 60);
    setDraftErr("");
    setDraft(blankDraft(anchor, s, online));
  };
  const openEdit = (ev: Ev) => {
    if (!ev.canEdit) {
      say("You can only view this event.");
      return;
    }
    setDraftErr("");
    setDraft({
      id: ev.id,
      title: ev.title,
      calId: ev.calId,
      date: ev.recurring ? ev.seriesDate : ev.date,
      start: hhmm(ev.start),
      end: hhmm(ev.end),
      repeat: ev.repeat,
      was: ev.repeat,
      location: ev.location,
      online: ev.online,
      attendees: ev.attendees.join(", "),
      notes: ev.notes,
    });
  };
  const patchDraft = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const saveDraft = async () => {
    if (!draft || saving) return;
    const title = draft.title.trim();
    if (!title) {
      setDraftErr("Add a title for the event.");
      return;
    }
    if (!draft.date) {
      setDraftErr("Pick a date.");
      return;
    }
    if (!draft.calId) {
      setDraftErr("Pick a calendar.");
      return;
    }
    const s = toMin(draft.start);
    const e = toMin(draft.end);
    if (e <= s) {
      setDraftErr("The end time must be after the start time.");
      return;
    }
    const day = parseYmd(draft.date);
    const at = (m: number) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, m).toISOString();
    const known = draft.id === null ? [] : (events.find((x) => x.id === draft.id)?.guests ?? []);
    const attendees = draft.attendees
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean)
      .map((n) => {
        const g = known.find((k) => (k.name || k.email).toLowerCase() === n.toLowerCase());
        return g ? { name: g.name, email: g.email, userId: g.userId } : { name: n, email: "" }; // a name with no e-mail is listed but never e-mailed
      });
    const body: Record<string, unknown> = {
      calendarId: draft.calId,
      title,
      description: draft.notes.trim(),
      location: draft.online && !draft.location.trim() ? TEAMS : draft.location.trim(),
      startAt: at(s),
      endAt: at(e),
      isAllDay: false,
      timeZone: TZ,
      onlineProvider: draft.online ? "Teams" : "None",
      attendees,
    };
    if (draft.repeat !== "custom" && (draft.id === null ? draft.repeat !== "none" : draft.repeat !== draft.was)) body.recurrence = draft.repeat;
    setSaving(true);
    try {
      if (draft.id === null) await call("POST", "/events", body);
      else await call("PATCH", `/events/${draft.id}?scope=all`, body);
      const created = draft.id === null;
      const when = draft.date;
      setDraft(null);
      say(created ? "Event created" : "Event updated");
      if (view !== "month" && !days.some((d) => ymd(d) === when)) goTo(parseYmd(when));
      void load(true);
    } catch (err) {
      setDraftErr(msgOf(err));
    } finally {
      setSaving(false);
    }
  };

  /** A repeating event asks first: this date only, or the whole series. */
  const askDelete = (ev: Ev) => {
    if (ev.recurring) setDelAsk(ev);
    else void removeEvent(ev, "all");
  };

  const undoDelete = async (id: string) => {
    try {
      await call("POST", `/events/${id}/restore`);
      say("Event restored");
    } catch (err) {
      say(msgOf(err));
    }
    void load(true);
  };

  const removeEvent = async (ev: Ev, scope: "this" | "all") => {
    setDelAsk(null);
    setPaneOpen(false);
    const prev = events;
    try {
      if (scope === "this") {
        setEvents((p) => p.filter((x) => !(x.id === ev.id && x.date === ev.date)));
        await call("DELETE", `/events/${ev.id}?scope=this&occurrence=${enc(ev.startAt)}`);
        say("Event deleted");
      } else {
        setEvents((p) => p.filter((x) => x.id !== ev.id));
        await call("DELETE", `/events/${ev.id}?undo=true`); // hidden for a short while; nobody is told until the Undo time has passed
        say("Event deleted", () => {
          void undoDelete(ev.id);
        });
      }
    } catch (err) {
      setEvents(prev);
      say(msgOf(err));
    }
  };

  const setLoc = (key: string, v: WorkLoc | null) => {
    const was = workLoc[key];
    const put = (val: WorkLoc | null | undefined) =>
      setWorkLoc((p) => {
        const n = { ...p };
        if (val) n[key] = val;
        else delete n[key];
        return n;
      });
    put(v);
    call("PUT", `/work-locations/${key}`, { location: v ?? "" }).catch((err) => {
      put(was);
      say(msgOf(err));
    });
  };

  const saveCal = async () => {
    if (!addCal) return;
    const name = addCal.name.trim();
    if (!name) return;
    try {
      await call("POST", "/calendars", { name, color: addCal.color, kind: "personal" });
      setAddCal(null);
      say(`“${name}” calendar added`);
      void load(true);
    } catch (err) {
      say(msgOf(err));
    }
  };

  const joinEvent = (ev: Ev) => {
    if (ev.onlineUrl) window.open(ev.onlineUrl, "_blank", "noopener");
    else say("This event has no meeting link yet.");
  };

  const doShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      say("Calendar link copied");
    } catch {
      say("Couldn’t copy the link");
    }
  };

  const doPrint = () => {
    const rows = days
      .flatMap((d) => (byDay.get(ymd(d)) ?? []).map((o) => `<tr><td>${esc(longDate(d))}</td><td>${hhmm(o.ev.start)} – ${hhmm(o.ev.end)}</td><td>${esc(o.ev.title)}</td><td>${esc(o.ev.location)}</td></tr>`))
      .join("");
    const w = window.open("", "_blank");
    if (!w) {
      say("Allow pop-ups to print");
      return;
    }
    w.document.write(
      `<!doctype html><title>${esc(label)}</title><style>body{font:14px Segoe UI,Arial,sans-serif;margin:24px;color:#242424}table{border-collapse:collapse;width:100%}td,th{border-bottom:1px solid #ddd;padding:6px 8px;text-align:left}</style><h2>${esc(label)}</h2><table><tr><th>Day</th><th>Time</th><th>Event</th><th>Location</th></tr>${rows || "<tr><td colspan=4>No events</td></tr>"}</table>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  const pickDate = (d: Date) => {
    goTo(d);
    setNavOpen(false);
  };

  const cols = days.length;
  const gridMin = 50 + cols * 120;

  /* one event block in the time grid */
  const evBlock = (o: Occ) => {
    const e = o.ev;
    const color = calById(e.calId)?.color ?? "#0f6cbd";
    const past = o.endAt.getTime() < now.getTime();
    const short = e.end - e.start <= 30;
    const id = `${e.id}|${o.key}`;
    const style = {
      top: (e.start / 60) * HOUR_H + 1,
      height: Math.max(((e.end - e.start) / 60) * HOUR_H - 2, 20),
      left: `calc(${(o.col / o.cols) * 100}% + 2px)`,
      width: `calc(${100 / o.cols}% - 6px)`,
      "--cc": color,
    } as React.CSSProperties;
    const sub = e.location || (e.organizer !== "You" ? e.organizer : "");
    return (
      <div
        key={id}
        role="button"
        tabIndex={0}
        aria-label={`${e.title}, ${hhmm(e.start)} to ${hhmm(e.end)}`}
        aria-pressed={selKey === id}
        className={`${styles.calEv} ${past ? styles.calEvPast : ""} ${short ? styles.calEvShort : ""} ${selKey === id ? styles.calEvSel : ""}`}
        style={style}
        onClick={() => openEvent(o)}
        onKeyDown={(k) => {
          if (k.key === "Enter" || k.key === " ") {
            k.preventDefault();
            openEvent(o);
          }
        }}
      >
        {short ? (
          <div className={styles.calEvLine}><b>{e.title}</b>{sub && <span> {sub}</span>}</div>
        ) : (
          <>
            <div className={styles.calEvTitle}>{e.title}</div>
            {e.location && <div className={styles.calEvSub}>{e.location}</div>}
            {e.organizer !== "You" && <div className={styles.calEvSub}>{e.organizer}</div>}
          </>
        )}
        {e.recurring && <ArrowRepeatAll16Regular className={styles.calEvRep} />}
      </div>
    );
  };

  return (
    <div className={`${styles.gU1Tf} ${styles.calRoot} ${navOpen ? styles.calNavOn : ""}`}>
      <div className={styles.jUsfu}>
        <div className={styles.QIl4y}>
          <div className={styles.O0VOp1}>
            <div className={styles.VvU3MOvL}>
              {/* Top action bar */}
              <div className={styles.clieTWU2V}>
                <div className={styles.clieTWU2Vc}>
                  <Button
                    shape="circular"
                    size="small"
                    className={`${styles.vgnei} ${styles.cgTactive}`}
                  >
                    <span className={`${styles.ci_menu_toggle} ${styles.border0} ${styles.hasImage}`}>
                      <img src={calender} alt="CRM" height={22} width={22} />
                    </span>
                    <span className={`${styles.ci_menu_brand} ${styles.dflex} ${styles.pe0}`}>
                      Calender
                    </span>
                  </Button>

                  <Button shape="circular" size="small" className={styles.Tab6re}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button className={styles.Tab6re} icon={<Timer16Regular />}>
                          Recent <ChevronDown16Regular />
                        </Button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem>My Channel</MenuItem>
                          <MenuItem>My Actions</MenuItem>
                          <MenuItem>Teams</MenuItem>
                          <MenuItem>Customers</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Button>

                  <Button size="small" shape="circular" className={styles.JKLow}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button className={styles.JKLow} icon={<Pin16Regular />}>
                          Pinned <ChevronDown16Regular />
                        </Button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem>Forcast</MenuItem>
                          <MenuItem>Channel</MenuItem>
                          <MenuItem>Leads</MenuItem>
                          <MenuItem>Actions</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Button>
                </div>

                <div className={styles.clieTWU2Vc}>
                  <Button shape="circular" size="small" className={styles.narTIl}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <MenuButton
                          icon={<Settings16Regular />}
                          className={styles.narTIl}
                          menuIcon={<ChevronDown16Regular />}
                        >
                          Actions
                        </MenuButton>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem icon={<ArrowDownloadRegular />}>Import Records</MenuItem>
                          <MenuItem icon={<DocumentArrowDownRegular />}>Spreadsheets</MenuItem>
                          <MenuItem icon={<DocumentFlowchartRegular />}>
                            Insert View in article
                          </MenuItem>
                          <MenuItem icon={<LinkRegular />}>Insert Link in article</MenuItem>
                          <MenuItem icon={<DocumentDataLinkRegular />}>
                            Link Menu in spread sheet
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Button>
                </div>
              </div>

              {/* Command bar */}
              <div className={styles.calBar} role="toolbar" aria-label="Calendar commands">
                <Button className={styles.calMenuBtn} appearance="subtle" size="small" icon={<Navigation20Regular />} aria-label="Calendars" onClick={() => setNavOpen((o) => !o)} />
                <div className={styles.calNew}>
                  <Button appearance="primary" size="small" icon={<CalendarAdd20Regular />} onClick={() => openNew()}>New event</Button>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button appearance="primary" size="small" icon={<ChevronDown16Regular />} aria-label="New event options" />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem icon={<CalendarAdd20Regular />} onClick={() => openNew()}>Event</MenuItem>
                        <MenuItem icon={<Video20Regular />} onClick={() => openNew(true)}>Online meeting</MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
                <span className={styles.calSep} />
                <Button className={`${styles.calBtn} ${view === "day" ? styles.calOn : ""}`} appearance="subtle" size="small" icon={<CalendarDay20Regular />} aria-pressed={view === "day"} onClick={() => setView("day")}>
                  Day
                </Button>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button className={styles.calChev} appearance="subtle" size="small" icon={<ChevronDown12Regular />} aria-label="Day options" />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {[1, 2, 3, 4].map((n) => (
                        <MenuItem key={n} onClick={() => { setDayN(n); setView("day"); }}>{n === 1 ? "Day" : `${n} days`}</MenuItem>
                      ))}
                    </MenuList>
                  </MenuPopover>
                </Menu>
                <Button className={`${styles.calBtn} ${view === "workweek" ? styles.calOn : ""}`} appearance="subtle" size="small" icon={<CalendarWorkWeek20Regular />} aria-pressed={view === "workweek"} onClick={() => setView("workweek")}>
                  Work week
                </Button>
                <Button className={`${styles.calBtn} ${view === "week" ? styles.calOn : ""}`} appearance="subtle" size="small" icon={<CalendarWeekStart20Regular />} aria-pressed={view === "week"} onClick={() => setView("week")}>
                  Week
                </Button>
                <Button className={`${styles.calBtn} ${view === "month" ? styles.calOn : ""}`} appearance="subtle" size="small" icon={<CalendarMonth20Regular />} aria-pressed={view === "month"} onClick={() => setView("month")}>
                  Month
                </Button>
                <Button className={styles.calBtn} appearance="subtle" size="small" icon={<SplitHorizontal20Regular />} disabled>
                  Split view
                </Button>
                <span className={styles.calSep} />
                <Menu checkedValues={{ kind: [filter] }} onCheckedValueChange={(_, d) => {
                  const v = d.checkedItems[0];
                  if (v === "all" || v === "recurring" || v === "once") setFilter(v);
                }}>
                  <MenuTrigger disableButtonEnhancement>
                    <Button className={`${styles.calBtn} ${filter !== "all" ? styles.calOn : ""}`} appearance="subtle" size="small" icon={<Filter20Regular />}>
                      {filter === "all" ? "Filter" : "Filter applied"}
                      <ChevronDown12Regular />
                    </Button>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItemRadio name="kind" value="all">All events</MenuItemRadio>
                      <MenuItemRadio name="kind" value="recurring">Recurring events</MenuItemRadio>
                      <MenuItemRadio name="kind" value="once">One-time events</MenuItemRadio>
                    </MenuList>
                  </MenuPopover>
                </Menu>
                <span className={styles.calSep} />
                <Button className={styles.calBtn} appearance="subtle" size="small" icon={<Share20Regular />} onClick={doShare}>
                  <span className={styles.calLbl}>Share</span>
                </Button>
                <Button className={styles.calBtn} appearance="subtle" size="small" icon={<Print20Regular />} onClick={doPrint}>
                  <span className={styles.calLbl}>Print</span>
                </Button>
              </div>

              {(status !== "ready" || live === "reconnecting") && (
                <div className={styles.calBanner} role="status">
                  <span>{status === "loading" ? "Loading your calendar…" : status === "signin" ? note || "Sign in to see your calendar." : status === "error" ? note : "Reconnecting to live updates…"}</span>
                  {status === "error" && <button type="button" onClick={() => void load()}>Try again</button>}
                </div>
              )}

              <div className={styles.rD9B8}>
                {/* Left sidebar */}
                <div className={styles.I1FTF}>
                  <div className={styles.EBXzq}>
                    <div className={`${styles.rxV0N} ${styles.a7Ps_}`}>
                      <div className={styles.UJUO4}>
                        <div className={styles.lp63L}>
                          <MiniMonth
                            cursor={cursor}
                            anchor={anchor}
                            today={today}
                            rangeStart={rangeStart}
                            rangeEnd={rangeEnd}
                            highlight={view !== "month"}
                            open={miniOpen}
                            onPick={pickDate}
                            onMove={(n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1))}
                            onToggle={() => setMiniOpen((o) => !o)}
                          />
                        </div>
                      </div>

                      <div className={styles.Oc1Jy}>
                        <div className={styles.SFJ5T}>
                          <div className={`${styles.MY30o} ${styles.addbordertop} ${styles.calSideBtns}`}>
                            <Button appearance="subtle" size="small" icon={<CalendarAdd20Regular />} onClick={() => setAddCal({ name: "", color: SWATCHES[0] ?? "#0f6cbd" })}>Add calendar</Button>
                            <Button appearance="subtle" size="small" icon={<CalendarCheckmark20Regular />} onClick={() => say("Your booking page will open once the booking API is connected")}>Go to my booking page</Button>
                          </div>

                          <div className={`${styles.AMOVa} ${styles.customScrollBar}`}>
                            <div className={`${styles.h96U3} ${styles.bzgJa}`}>
                              <ul>
                                <li className={`${styles.FVY0q} ${styles.MY30o}`}>
                                  <div className={styles.GsziR}>
                                    <Accordion collapsible defaultOpenItems="1">
                                      <AccordionItem value="1">
                                        <AccordionHeader expandIconPosition="start">My calendars</AccordionHeader>
                                        <AccordionPanel>
                                          <div
                                            className={`${styles.msFocusZone} ${styles.css168} ${styles.q1a_R}`}
                                          >
                                            {cals.map((c) => (
                                              <div key={c.id} className={styles.GsziR}>
                                                <div className={`${styles.feQ6T} ${styles.yxmiB}`}>
                                                  <button
                                                    type="button"
                                                    role="checkbox"
                                                    aria-checked={c.on}
                                                    className={styles.calCalRow}
                                                    onClick={() => setCals((p) => p.map((x) => (x.id === c.id ? { ...x, on: !x.on } : x)))}
                                                  >
                                                    <span className={styles.calChk} style={{ color: c.on ? c.color : "#8a8886" }}>
                                                      {c.on ? <CheckmarkCircle20Filled /> : <Circle20Regular />}
                                                    </span>
                                                    <span className={styles.calCalName2}>{c.name}</span>
                                                  </button>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </AccordionPanel>
                                      </AccordionItem>
                                    </Accordion>
                                    <button type="button" className={styles.calShowAll} onClick={() => setCals((p) => p.map((c) => ({ ...c, on: true })))}>Show all</button>
                                  </div>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.calScrim} onClick={() => setNavOpen(false)} />

                {/* Main calendar area */}
                <div className={styles.RBJZN}>
                  <div className={`${styles.Ugldg} ${styles.css297}`}>
                    <div className={`${styles.ZlutZ} ${paneOpen ? styles.calPaneOn : ""}`}>
                      <div className={styles.PddJU}>
                        {/* Toolbar */}
                        <div className={styles.d8LIg}>
                          <div className={styles.calHdr}>
                            <Button className={styles.calTodayBtn} size="small" appearance="subtle" icon={<CalendarToday20Regular />} onClick={() => goTo(today)}>Today</Button>
                            <Button size="small" appearance="subtle" icon={<ChevronLeft20Regular />} aria-label="Previous" onClick={() => step(-1)} />
                            <Button size="small" appearance="subtle" icon={<ChevronRight20Regular />} aria-label="Next" onClick={() => step(1)} />
                            <Popover open={jumpOpen} onOpenChange={(_, d) => setJumpOpen(d.open)} positioning="below-start">
                              <PopoverTrigger disableButtonEnhancement>
                                <Button className={styles.calRange} size="small" appearance="subtle" iconPosition="after" icon={<ChevronDown12Regular />} title={`${label}, Jump to a specific date or range`}>
                                  {label}
                                </Button>
                              </PopoverTrigger>
                              <PopoverSurface className={styles.calJump}>
                                <MiniMonth
                                  cursor={cursor}
                                  anchor={anchor}
                                  today={today}
                                  rangeStart={rangeStart}
                                  rangeEnd={rangeEnd}
                                  highlight={view !== "month"}
                                  open
                                  hideToggle
                                  onPick={(d) => { pickDate(d); setJumpOpen(false); }}
                                  onMove={(n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1))}
                                  onToggle={() => undefined}
                                />
                              </PopoverSurface>
                            </Popover>
                          </div>
                        </div>

                        {/* Calendar grid */}
                        <div className={styles.iaCtK}>
                          <div className={styles.ONUhz}>
                            <div className={styles.BbwdT}>
                              {view === "month" ? (
                                <div className={styles.calMonth}>
                                  <div className={styles.calMonthDow}>
                                    {DOW_SHORT.map((n) => <span key={n}>{n}</span>)}
                                  </div>
                                  <div className={styles.calMonthGrid}>
                                    {days.map((d) => {
                                      const list = byDay.get(ymd(d)) ?? [];
                                      const isToday = sameDay(d, today);
                                      return (
                                        <div key={ymd(d)} className={`${styles.calCell} ${d.getMonth() !== anchor.getMonth() ? styles.calCellOut : ""}`}>
                                          <button
                                            type="button"
                                            className={`${styles.calCellNum} ${isToday ? styles.calCellToday : ""}`}
                                            aria-label={`${longDate(d)}, open day`}
                                            onClick={() => { setDayN(1); setView("day"); goTo(d); }}
                                          >
                                            {d.getDate() === 1 ? `${(MONTHS[d.getMonth()] ?? "").slice(0, 3)} 1` : d.getDate()}
                                          </button>
                                          {list.slice(0, list.length > 3 ? 2 : 3).map((o) => (
                                            <button
                                              key={`${o.ev.id}|${o.key}`}
                                              type="button"
                                              className={`${styles.calChip} ${o.endAt.getTime() < now.getTime() ? styles.calChipPast : ""}`}
                                              style={{ "--cc": calById(o.ev.calId)?.color ?? "#0f6cbd" } as React.CSSProperties}
                                              onClick={() => openEvent(o)}
                                            >
                                              <i />
                                              <span>{hhmm(o.ev.start)} {o.ev.title}</span>
                                            </button>
                                          ))}
                                          {list.length > 3 && (
                                            <button type="button" className={styles.calMore} onClick={() => { setDayN(1); setView("day"); goTo(d); }}>+{list.length - 2} more</button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <div ref={scrollRef} className={`${styles.VvYef} ${styles.Wth9e} ${styles.calScroll}`}>
                                  {/* Day headers */}
                                  <div className={`${styles.vuk60} ${styles.calHead}`}>
                                    <div className={`${styles.jJuEc} ${styles.calHeadRow}`} style={{ minWidth: gridMin }}>
                                      <div className={styles.calGutter} />
                                      {days.map((d) => {
                                        const k = ymd(d);
                                        const loc = workLoc[k];
                                        const isToday = sameDay(d, today);
                                        return (
                                          <div key={k} className={`${styles.B5Pd6} ${styles.Xky5j} ${styles.calDayHead} ${isToday ? styles.calDayToday : ""}`}>
                                            <button type="button" className={styles.calDayBtn} aria-label={`${longDate(d)}, show details`} onClick={() => openDay(d)}>
                                              <span className={styles.calDayNum}>{d.getDate()}</span>
                                              <span className={styles.calDayName}>{DOW_SHORT[d.getDay()]}</span>
                                            </button>
                                            <Menu>
                                              <MenuTrigger disableButtonEnhancement>
                                                <Button
                                                  appearance="subtle"
                                                  size="small"
                                                  className={`${styles.calLocBtn} ${loc ? styles.calLocOn : ""}`}
                                                  icon={loc === "home" ? <Home20Filled /> : loc === "office" ? <Building20Filled /> : <Location20Regular />}
                                                  aria-label={loc === "home" ? "Working from home" : loc === "office" ? "Working from the office" : "Set work location"}
                                                />
                                              </MenuTrigger>
                                              <MenuPopover>
                                                <MenuList>
                                                  <MenuItem icon={<Home20Filled />} onClick={() => setLoc(k, "home")}>Home</MenuItem>
                                                  <MenuItem icon={<Building20Filled />} onClick={() => setLoc(k, "office")}>Office</MenuItem>
                                                  <MenuDivider />
                                                  <MenuItem onClick={() => setLoc(k, null)}>Clear</MenuItem>
                                                </MenuList>
                                              </MenuPopover>
                                            </Menu>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Time + events grid */}
                                  <div className={`${styles.DBT5H} ${styles.Wth9e} ${styles.rClUV} ${styles.calBody}`} style={{ minWidth: gridMin }}>
                                    <div className={styles.calGutter} aria-hidden="true">
                                      {HOURS.map((h) => (
                                        <div key={h} className={styles.calGutterHr}>{h > 0 && <span>{h}</span>}</div>
                                      ))}
                                    </div>
                                    {days.map((d) => {
                                      const k = ymd(d);
                                      const isToday = sameDay(d, today);
                                      return (
                                        <div
                                          key={k}
                                          className={styles.calCol}
                                          onDoubleClick={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            const r = e.currentTarget.getBoundingClientRect();
                                            const m = Math.floor(((e.clientY - r.top) / HOUR_H) * 2) * 30;
                                            setDraftErr("");
                                            setDraft(blankDraft(d, m));
                                          }}
                                        >
                                          {HOURS.map((h) => <div key={h} className={styles.calHr} />)}
                                          {(byDay.get(k) ?? []).map(evBlock)}
                                          {isToday && <div className={styles.calNow} style={{ top: ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_H }} />}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Slide-out details panel (empty state kept from original) */}
                      <div className={`${styles.IWkMv} ${styles.calPanel} ${paneOpen ? styles.calPanelOpen : ""}`} aria-hidden={!paneOpen} role="complementary" aria-label="Details">
                        <div className={styles.MoZcH}>
                          <div className={styles.ow0Pu}>
                            <div className={styles.INsig}>
                              <div className={`${styles.otKC_} ${styles.r6Bqo}`}>
                                <div className={styles.ti0lx}>{paneDay ? shortDate(paneDay) : ""}</div>
                                <Button className={styles.calClose} appearance="subtle" size="small" icon={<Dismiss20Regular />} aria-label="Close details" onClick={() => setPaneOpen(false)} />
                              </div>
                              <div className={`${styles.ywuFU} ${styles.customScrollBar}`}>
                                <div className={styles.z_NaR}>
                                  {pane && pane.kind === "event" && paneOcc ? (
                                    <EventDetails
                                      occ={paneOcc}
                                      cal={calById(paneOcc.ev.calId)}
                                      onEdit={() => openEdit(paneOcc.ev)}
                                      onDelete={() => askDelete(paneOcc.ev)}
                                      onJoin={() => joinEvent(paneOcc.ev)}
                                    />
                                  ) : paneList.length > 0 ? (
                                    <ul className={styles.calAgenda}>
                                      {paneList.map((o) => (
                                        <li key={`${o.ev.id}|${o.key}`}>
                                          <button type="button" onClick={() => openEvent(o)} style={{ "--cc": calById(o.ev.calId)?.color ?? "#0f6cbd" } as React.CSSProperties}>
                                            <i />
                                            <span><b>{o.ev.title}</b><em>{hhmm(o.ev.start)} – {hhmm(o.ev.end)}{o.ev.location ? ` · ${o.ev.location}` : ""}</em></span>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <div
                                      className={`${styles.msShimmercontainer} ${styles.Jc2NK} ${styles.root326}`}
                                    >
                                      <div
                                        className={`${styles.msShimmerdataWrapper} ${styles.dataWrapper362}`}
                                      >
                                        <div className={styles.w7Dk5}>
                                          <div
                                            className={`${styles.msFocusZone} ${styles.css169} ${styles.UFEPK}`}
                                          >
                                            <div className={styles.ei__1}>
                                              <div className={styles.B1_rI}>
                                                <div
                                                  className={`${styles.w1MGJ} ${styles.p7ngA} ${styles.F2jxh}`}
                                                >
                                                  <img
                                                    width="120px"
                                                    height="120px"
                                                    src={NOBOOKEDINGITEM}
                                                    alt=""
                                                  />
                                                  <span
                                                    className={`${styles.ksePc} ${styles.pXbOE}`}
                                                    id="EmptyState_MainMessage"
                                                  >
                                                    No body checked In for the day
                                                  </span>
                                                  <span
                                                    className={`${styles.TrKke} ${styles.NZBAE}`}
                                                  >
                                                    Enjoy!
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={`${styles.AZjDG} ${styles.X7l1H}`}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {toast && (
                <div className={styles.calToast} role="status">
                  <span>{toast.text}</span>
                  {toast.undo && <button type="button" onClick={() => { toast.undo?.(); setToast(null); }}>Undo</button>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <EventDialog
        draft={draft}
        cals={cals.filter((c) => c.canEdit || c.id === draft?.calId)}
        error={draftErr}
        recurring={draft !== null && draft.repeat !== "none"}
        onChange={patchDraft}
        onSave={() => void saveDraft()}
        onCancel={() => setDraft(null)}
      />

      <Dialog open={delAsk !== null} onOpenChange={(_, d) => { if (!d.open) setDelAsk(null); }}>
        <DialogSurface className={styles.calDialogSm}>
          {delAsk && (
            <DialogBody>
              <DialogTitle>Delete repeating event</DialogTitle>
              <DialogContent>“{delAsk.title}” repeats. What do you want to delete?</DialogContent>
              <DialogActions>
                <Button appearance="secondary" size="small" onClick={() => setDelAsk(null)}>Cancel</Button>
                <Button appearance="secondary" size="small" onClick={() => void removeEvent(delAsk, "this")}>Only this event</Button>
                <Button appearance="primary" size="small" onClick={() => void removeEvent(delAsk, "all")}>All events</Button>
              </DialogActions>
            </DialogBody>
          )}
        </DialogSurface>
      </Dialog>

      <Dialog open={addCal !== null} onOpenChange={(_, d) => { if (!d.open) setAddCal(null); }}>
        <DialogSurface className={styles.calDialogSm}>
          {addCal && (
            <DialogBody>
              <DialogTitle>Add calendar</DialogTitle>
              <DialogContent>
                <Field label="Name" required>
                  <Input autoFocus value={addCal.name} placeholder="Calendar name" onChange={(_, d) => setAddCal({ ...addCal, name: d.value })} onKeyDown={(e) => { if (e.key === "Enter") void saveCal(); }} />
                </Field>
                <div className={styles.calSwatches} role="radiogroup" aria-label="Colour">
                  {SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={addCal.color === c}
                      aria-label={c}
                      className={`${styles.calSwatch} ${addCal.color === c ? styles.calSwatchOn : ""}`}
                      style={{ background: c }}
                      onClick={() => setAddCal({ ...addCal, color: c })}
                    />
                  ))}
                </div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" size="small" onClick={() => setAddCal(null)}>Cancel</Button>
                <Button appearance="primary" size="small" icon={<Add20Regular />} onClick={() => void saveCal()}>Add</Button>
              </DialogActions>
            </DialogBody>
          )}
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default CilpronCalenders;